/**
 * Whether a supplier email may file itself, and what filing one by hand does
 * (ADR 0311). The fourth decision `supplierMail.ts` describes: an email is
 * filed on an order by itself only when it came from that order's supplier
 * (`confirmSupplierSender`). Split out of `supplierMail.ts` to keep each
 * file readable; the Edge Function copy in
 * `supabase/functions/orders-inbox/filing.ts` still covers the sender rules.
 */

import type {
  SupplierMailPlan,
  SupplierMailReviewReason,
} from "./supplierMail.js";

/**
 * Mail providers anybody can sign up to. A supplier whose address on file is
 * at one of these is matched by the whole address, never the domain: a
 * stranger's gmail.com is not ABC Supply's rep.
 */
const PUBLIC_MAIL_DOMAINS: ReadonlySet<string> = new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
  "live.com",
  "msn.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "comcast.net",
  "att.net",
  "proton.me",
  "protonmail.com",
]);

function domainOf(address: string): string | null {
  const at = address.lastIndexOf("@");
  return at > 0 && at < address.length - 1 ? address.slice(at + 1) : null;
}

/**
 * Whether an email came from the supplier on an order: the sender's domain is
 * the domain of an address on file for that supplier, or, when that address
 * is at a public mail provider, the sender is that very address. No address
 * on file means nothing can vouch for the sender, so the answer is no.
 *
 * The field may hold more than one address, separated by commas, semicolons
 * or spaces; any of them vouches.
 */
export function senderMatchesSupplier(
  fromAddress: string | null | undefined,
  supplierEmail: string | null | undefined,
): boolean {
  const from = (fromAddress ?? "").trim().toLowerCase();
  const fromDomain = domainOf(from);
  if (!fromDomain) return false;
  const onFile = (supplierEmail ?? "")
    .toLowerCase()
    .split(/[\s,;]+/)
    .filter((a) => a.includes("@"));
  return onFile.some((address) => {
    const domain = domainOf(address);
    if (!domain) return false;
    return PUBLIC_MAIL_DOMAINS.has(domain)
      ? address === from
      : domain === fromDomain;
  });
}

/** The receiving server Gmail names in the `Authentication-Results` it adds. */
const GMAIL_AUTHSERV_ID = "mx.google.com";

/** A header's text with its (comments) taken out, innermost first. */
function withoutComments(value: string): string {
  let out = value;
  for (let previous = ""; previous !== out;) {
    previous = out;
    out = out.replace(/\([^()]*\)/g, " ");
  }
  return out;
}

/**
 * Whether Gmail proved the email came from the domain in its From line.
 *
 * The From line is whatever the sender typed. What vouches for it is the
 * `Authentication-Results` header Gmail adds on receipt: DMARC passed for
 * that domain, a DKIM signature by that very domain checked out, or, for an
 * email the orders@ group re-sent, Gmail's sealed record of the same checks
 * passing when the group first received it (`arcVouchesFor`), read only
 * when the last hop really was the group's (`viaGroup`). Only the
 * topmost such header is read, and only when Gmail wrote it: Gmail puts its
 * own on top, and anything below it arrived with the message and could say
 * anything. No header, or one that proves nothing, is no.
 *
 * `authenticationResults` is every `Authentication-Results` value, in the
 * order the message carries them.
 */
export function senderAuthenticated(
  fromAddress: string | null | undefined,
  authenticationResults: readonly string[],
  groupAddress?: string | null,
): boolean {
  const fromDomain = domainOf((fromAddress ?? "").trim().toLowerCase());
  const top = authenticationResults[0];
  if (!fromDomain || top === undefined) return false;
  const [authserv, ...results] = resultsOf(top.toLowerCase());
  const authservId = withoutComments(authserv ?? "")
    .trim()
    .split(/\s+/)[0];
  if (authservId !== GMAIL_AUTHSERV_ID) return false;
  const plain = results.map((r) =>
    withoutComments(r).replace(/\s+/g, " ").trim(),
  );
  if (plain.some((r) => directlyVouches(r, fromDomain))) return true;

  // The sealed record counts only on the group's own re-send: the last hop
  // passed SPF for the group's bounce address (orders+...@), which only
  // Google's servers re-sending for the group use. An email sent straight
  // to a mailbox with "To: orders@" typed on it carries whatever seal its
  // sender made, and Gmail reports that seal too, so it is not read; nor is
  // one sent from a staff mailbox at the same domain, which passes SPF for
  // the domain but is not the group's bounce address.
  const group = (groupAddress ?? "").trim().toLowerCase();
  if (!domainOf(group) || !plain.some((r) => viaGroup(r, group))) return false;
  const sealed = plain.findIndex((r) => verdictOf(r) === "arc=pass");
  if (sealed < 0) return false;
  const comment = /\(([^()]*)\)/.exec(results[sealed] ?? "")?.[1] ?? "";
  return arcVouchesFor(comment, fromDomain);
}

/** The header's results, split at the semicolons outside comments, comments kept. */
function resultsOf(header: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let current = "";
  for (const ch of header) {
    if (ch === "(") depth += 1;
    else if (ch === ")") depth = Math.max(0, depth - 1);
    if (ch === ";" && depth === 0) {
      out.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  out.push(current.trim());
  return out;
}

function verdictOf(result: string): string {
  return result.split(/\s+/)[0] ?? "";
}

function propertyOf(result: string, name: string): string | undefined {
  return result
    .split(/\s+/)
    .slice(1)
    .find((p) => p.startsWith(`${name}=`))
    ?.slice(name.length + 1)
    .replace(/^"|"$/g, "");
}

/** DMARC passed for the From domain, or a DKIM signature by that very domain. */
function directlyVouches(result: string, fromDomain: string): boolean {
  const verdict = verdictOf(result);
  if (verdict === "dmarc=pass") {
    return propertyOf(result, "header.from") === fromDomain;
  }
  if (verdict === "dkim=pass") {
    // Gmail writes the signer as header.i=@domain; others as header.d=.
    const identity = propertyOf(result, "header.i");
    const signer =
      propertyOf(result, "header.d") ??
      identity?.slice(identity.lastIndexOf("@") + 1);
    return signer === fromDomain;
  }
  return false;
}

/**
 * The last hop was the group's re-send: SPF passed for the group's bounce
 * address, which is the group's own name with "+" and a code after it, at
 * the group's domain ("orders+bncBCU3...@copperridge-roofing.com").
 */
function viaGroup(result: string, groupAddress: string): boolean {
  if (verdictOf(result) !== "spf=pass") return false;
  const mailFrom = propertyOf(result, "smtp.mailfrom") ?? "";
  const at = groupAddress.lastIndexOf("@");
  const mailAt = mailFrom.lastIndexOf("@");
  if (at <= 0 || mailAt <= 0) return false;
  return (
    mailFrom.slice(mailAt + 1) === groupAddress.slice(at + 1) &&
    mailFrom.slice(0, mailAt).startsWith(`${groupAddress.slice(0, at)}+`)
  );
}

/**
 * What Gmail found when the email first arrived, before the orders@ group
 * re-sent it. On that last hop SPF is for the group's own address and DMARC
 * fails for any supplier that does not sign its mail, which is most of
 * them. Gmail keeps the first hop's result under seal (ARC) and, when the
 * seal checks out, writes it after `arc=pass` as a comment: "arc=pass (i=2
 * spf=pass spfdomain=heelybrown.com dmarc=pass fromdomain=heelybrown.com)".
 * The same two things vouch as on a direct email: DMARC passing for the
 * From line's domain, or a signature by it. SPF alone does not, there or
 * here. `comment` is the text inside that comment.
 */
function arcVouchesFor(comment: string, fromDomain: string): boolean {
  const found = new Map<string, string>();
  for (const token of comment.split(/\s+/)) {
    const eq = token.indexOf("=");
    if (eq > 0) found.set(token.slice(0, eq), token.slice(eq + 1));
  }
  const ties = (verdict: string, domain: string): boolean =>
    found.get(verdict) === "pass" && found.get(domain) === fromDomain;
  return ties("dmarc", "fromdomain") || ties("dkim", "dkdomain");
}

/**
 * The plan, with every filing whose order's supplier did not send the email
 * taken back off and the email sent to review. An MO number is sequential
 * and easy to guess, so naming one is not enough to put a document on an
 * order under the supplier's name, where its prices can be checked and
 * copied onto the order (ADR 0311). Nor is a From line that names the
 * supplier, which anybody can type: `authenticated` is
 * `senderAuthenticated`'s answer for this email. A person files it from
 * Tools → Supplier Mail after looking at who sent it. A forwarded copy of a
 * document already on the job stays recorded as one, whoever forwarded it.
 */
export function confirmSupplierSender(
  plan: SupplierMailPlan,
  fromAddress: string | null | undefined,
  authenticated: boolean,
): SupplierMailPlan {
  const held = new Set<SupplierMailReviewReason>();
  // A recognised copy writes nothing new, so whoever forwarded it may.
  const filings = plan.filings.filter((f) => {
    if (f.duplicate) return true;
    if (!senderMatchesSupplier(fromAddress, f.order.supplier_email)) {
      held.add("sender_not_supplier");
      return false;
    }
    if (!authenticated) {
      held.add("sender_unverified");
      return false;
    }
    return true;
  });
  if (held.size === 0) return plan;
  const reasons = [...plan.reasons];
  for (const reason of held)
    if (!reasons.includes(reason)) reasons.push(reason);
  return { status: "review", reasons, filings };
}

/** One of a waiting email's files, as filing it by hand needs it. */
export interface ManualFilingFile {
  id: string;
  content_sha256?: string | null;
  /** Already on Docs: the function filed it, under a kind or order now being corrected. */
  job_file_id: string | null;
}

export interface ManualFilingStep {
  fileId: string;
  /**
   * `register`: a new register entry. `update`: correct the entry already
   * there to the kind and order chosen. `duplicate`: the same bytes are on
   * that job already, so record it and file nothing.
   */
  action: "register" | "update" | "duplicate";
  /** For the title, from 1, over the files that are on Docs once filed. */
  sequence: number;
}

/**
 * What filing a waiting email by hand does to each of its files, on the job
 * a person chose (ADR 0311).
 *
 * The same forwarded-copy rule the function applies: a file whose bytes are
 * already on that job (`filedHashes`, by job, as `planSupplierMail` takes it)
 * is recorded, not filed twice. A file already on Docs is corrected in place
 * rather than left under the kind or order the function gave it. An email
 * with no file kept files nothing, so it is refused: there would be nothing
 * on the job to show for it.
 */
export function planManualSupplierFiling(input: {
  files: readonly ManualFilingFile[];
  jobId: string;
  filedHashes?: ReadonlyMap<string, ReadonlySet<string>>;
}): ManualFilingStep[] {
  if (input.files.length === 0) {
    throw new Error(
      "This email has no file kept yet, so there is nothing to file.",
    );
  }
  const onJob = input.filedHashes?.get(input.jobId);
  let sequence = 0;
  return input.files.map((file) => {
    if (file.job_file_id) {
      sequence += 1;
      return { fileId: file.id, action: "update", sequence };
    }
    if (file.content_sha256 && onJob?.has(file.content_sha256)) {
      return { fileId: file.id, action: "duplicate", sequence: 0 };
    }
    sequence += 1;
    return { fileId: file.id, action: "register", sequence };
  });
}

/**
 * Whether a checked document's prices may be put on the order in one press
 * (ADR 0311). An invoice, or a receipt uploaded by hand (ADR 0145), says what
 * the order cost, and a credit memo is offered the same button: Jonathon,
 * 2026-10-27, chose "Invoices and credit memos" ("1. b"). An acknowledgement
 * confirms nothing about price, so it is never copied onto the order.
 */
export function pricesBelongOnOrder(kind: string | null | undefined): boolean {
  return (
    kind === "supplier_invoice" ||
    kind === "supplier_receipt" ||
    kind === "supplier_credit_memo"
  );
}
